// Shared Razorpay Checkout helper — used by every payment surface
// (customer wallet, checkout, tenant-admin wallet, super-admin plan top-up).
// Branches between web Checkout.js and the native `capacitor-razorpay`
// plugin (Android/iOS apps load this same remote web bundle inside a
// Capacitor WebView — see build_app.py's capacitor_scaffold — so the native
// bridge call has to live here in the shared web bundle, not in a separate
// native-only codebase). Every caller gets the same resolve/reject contract
// either way.
//
// NOTE: the native path has not been verified against a real Android/iOS
// build+device — it's implemented against the plugin's documented API
// (github.com/razorpay/razorpay-capacitor, v1.2.1) but Capacitor plugin
// bridges can have platform-specific quirks in practice. Test on a real
// device before shipping.
import { Capacitor } from "@capacitor/core";
import api from "./api";

const NETWORK_TIMEOUT_MS = 15000;

function timeout(ms, message) {
  return new Promise((_, reject) => setTimeout(() => reject({ failed: true, description: message }), ms));
}

let _scriptPromise = null;
function ensureRazorpayScript() {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = Promise.race([
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve();
      s.onerror = () => reject({ failed: true, description: "Couldn't reach Razorpay — check your connection and try again." });
      document.body.appendChild(s);
    }),
    timeout(NETWORK_TIMEOUT_MS, "Payment gateway took too long to load — check your connection and try again."),
  ]).catch((err) => { _scriptPromise = null; throw err; });
  return _scriptPromise;
}

/**
 * Fetches a Razorpay order (or a mock-mode response) from the given
 * `GET .../razorpay-order` endpoint. Every payment surface's order-creation
 * step goes through here so network/timeout handling is defined once.
 * Backend validation errors (4xx/5xx with a response body) pass through
 * unchanged — callers already read `e.response?.data?.detail`. A request
 * that never gets a response (offline, DNS failure, dropped connection,
 * server hang) is normalized to this module's {failed, description} shape.
 */
export async function fetchRazorpayOrder(url) {
  try {
    const { data } = await api.get(url, { timeout: NETWORK_TIMEOUT_MS });
    return data;
  } catch (err) {
    if (!err.response) {
      // No HTTP response at all — offline, timeout, DNS failure, dropped
      // connection mid-request. A real backend error (400/500 with a body)
      // is left alone so callers' existing `e.response?.data?.detail` handling keeps working.
      throw { failed: true, description: "Network error — check your connection and try again." };
    }
    throw err;
  }
}

async function openNativeCheckout({ key, amount, currency, order_id, name, description, theme, prefill }) {
  const { Checkout } = await Promise.race([
    import("capacitor-razorpay"),
    timeout(NETWORK_TIMEOUT_MS, "Payment module failed to load — check your connection and try again."),
  ]);
  try {
    // No client-side timeout on the checkout call itself — the user may be
    // legitimately mid-flow (entering a card, waiting on a 3D-Secure OTP)
    // for minutes. Razorpay's own SDK/gateway owns session-expiry and
    // reports it through the same failure path we already normalize below.
    const data = await Checkout.open({
      key, amount: String(amount), currency, order_id, name, description,
      prefill, theme: theme || { color: "#3A86FF" },
    });
    // The plugin's documented response shape is inconsistent about whether
    // `response` arrives as a JSON string or an already-parsed object —
    // handle both.
    let res = data?.response;
    if (typeof res === "string") {
      try { res = JSON.parse(res); } catch { /* leave as string */ }
    }
    if (!res?.razorpay_payment_id) throw { failed: true, description: "Payment did not return a valid response" };
    return res;
  } catch (err) {
    // Capacitor's own bridge throws this exact shape — not a payment
    // failure at all, but the native "Checkout" plugin never having been
    // registered for this build (either the installed app predates the
    // native razorpay-capacitor wiring, or `cap add android` ran before
    // build_app.py's MainActivity patch could apply — see
    // patch_mainactivity_register_razorpay). Surfacing the raw
    // "\"Checkout\" plugin is not implemented on Android" string to the
    // end user is exactly the kind of runtime exception that must never
    // reach them directly — catch it first and give an actionable message
    // instead of falling through to the generic parsing below, which would
    // otherwise treat that raw string as if it were already user-facing.
    const rawMessage = err instanceof Error ? err.message : (typeof err === "string" ? err : "");
    if (/is not implemented on/i.test(rawMessage)) {
      throw {
        failed: true,
        description: "Online payment isn't available in this app version yet. Please update the app, or pay from a web browser instead.",
      };
    }
    // Cancel/failure reject as a plain string, a plain object, OR (observed
    // on real Android devices via razorpay-capacitor) an Error whose
    // .message is ITSELF the JSON-stringified {code, description, source,
    // step, reason, metadata} native error — try parsing whichever string
    // we end up with, don't just take it at face value.
    let parsed = err;
    if (typeof err === "string") {
      try { parsed = JSON.parse(err); } catch { parsed = { description: err }; }
    } else if (err instanceof Error) {
      try { parsed = JSON.parse(err.message); } catch { parsed = { description: err.message }; }
    }
    if (parsed?.cancelled || parsed?.failed) throw parsed;
    if (parsed?.code === 2 || /cancel/i.test(parsed?.description || "")) throw { cancelled: true };
    // Razorpay's native SDK sometimes sends description as the literal
    // string "undefined" (seen directly on device) — that's not a real
    // message, don't show it as one.
    const desc = parsed?.description;
    const friendly = (desc && desc !== "undefined") ? desc : "Payment failed. Please try again.";
    throw { failed: true, description: friendly };
  }
}

/**
 * Opens Razorpay Checkout for a previously-created order and waits for the result.
 * Resolves with {razorpay_payment_id, razorpay_order_id, razorpay_signature} on success.
 * Rejects with {cancelled: true} if the user dismisses the modal, or
 * {failed: true, description} if Razorpay reports a payment failure.
 */
export async function openRazorpayCheckout({ key, amount, currency = "INR", order_id, name, description, theme, prefill }) {
  if (Capacitor.isNativePlatform()) {
    return openNativeCheckout({ key, amount, currency, order_id, name, description, theme, prefill });
  }
  await ensureRazorpayScript();
  return new Promise((resolve, reject) => {
    let settled = false;
    const rzp = new window.Razorpay({
      key, amount, currency, order_id, name, description, prefill,
      theme: theme || { color: "#3A86FF" },
      handler: (res) => { settled = true; resolve(res); },
      modal: {
        ondismiss: () => { if (!settled) reject({ cancelled: true }); },
      },
    });
    rzp.on("payment.failed", (resp) => {
      settled = true;
      const desc = resp?.error?.description;
      reject({ failed: true, description: (desc && desc !== "undefined") ? desc : "Payment failed. Please try again." });
    });
    rzp.open();
  });
}
